"use client";
import { useQueryStorage } from "@/contexts/QueryStorageContext";
import { Content } from "@/screens/Welcome/common/Content";
import { useTranslation } from "@/i18n/client";
import { useRouter, useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { createContract } from "@/requests/contract/create";
import { notifications } from "@mantine/notifications";
import AppShell from "@/components/AppShell";
import Link from "@/components/Link";
import { Trans } from "react-i18next";
import { useProcessRouter } from "@/router/useProcessRouter";
import { Process, Contract } from "@/types/contract";
import classes from "./page.module.css";

const WelcomeNewContractPage = () => {
  const { t } = useTranslation(["pageHint", "welcome"]);

  const { process, commodity, product, customerType, contractSource, segment, ...rest } = useQueryStorage().storedQueries;
  const searchParams = useSearchParams();
  const router = useRouter();
  const processRouter = useProcessRouter();

  const createContractMutation = useMutation({
    mutationFn: createContract,
    onSuccess: data => {
      const contract = {
        process: process?.value,
        selfService: true,
        uuid: data.contractUuid,
        contractDetails: {
          identificationFeActivation: false,
        },
      };
      const nextUrl = processRouter.getNextUrl(contract as unknown as Contract);

      if (nextUrl) {
        router.replace(`${nextUrl}?${searchParams.toString()}`);
      }
    },
    onError: () => {
      notifications.show({
        title: t("createContractNotSuccessful"),
        message: "",
        color: "red",
      });
    },
  });

  if (
    !commodity ||
    !process ||
    !contractSource ||
    (process.value !== Process.NEW_POINT && process.value !== Process.ZDP && !product)
  ) {
    throw new Error("requiredParamNotProvided");
  }

  const handleStart = () => {
    createContractMutation.mutate({
      productId: product?.id ?? null,
      process: process.value,
      commodity: commodity.value,
      customerType,
      contractSource,
      segment,
      ...rest,
    });
  };

  return (
    <AppShell
      hintMessage={
        <Trans
          t={t}
          i18nKey="pageHint:helpStepWelcome"
          components={{
            linkCompanyCalculation: (
              <Link key="linkCompanyCalculation" href="https://www.eon.cz/firemni-zakaznici/zivnostnici/chci-eon/#kalkulace" />
            ),
          }}
        />
      }
      contentStyles={classes.appShell}>
      <Content
        selfService
        commodity={commodity}
        process={process}
        customerType={customerType}
        product={product}
        onStart={handleStart}
        isLoading={createContractMutation.isPending}
        segment={segment}
      />
    </AppShell>
  );
};

export default WelcomeNewContractPage;
